import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getProduttori, getProduttoriDaRivedereSummary } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Search, AlertTriangle, ChevronRight, PhoneOff, MapPinOff,
  ArrowUpDown, ArrowDownUp, ArrowDown01, ArrowUp10, RotateCw,
} from "lucide-react";
import { FREQUENZE, FREQUENZE_LABEL, SETTIMANE_MESE, formatDateIt } from "../lib/constants";
import { useZoneAttive } from "../hooks/useZone";

const AREE = [
  { v: "A_NORMALE",    label: "A — Normale", color: "#1B5E20" },
  { v: "B_ESTESA",     label: "B — Estesa",  color: "#F57C00" },
  { v: "C_FUORI_AREA", label: "C — Fuori area", color: "#C62828" },
];

const SORT_OPTIONS = [
  { v: "ragioneSociale",     label: "Ragione sociale" },
  { v: "zona",               label: "Zona" },
  { v: "areaClassificazione",label: "Area" },
  { v: "frequenza",          label: "Frequenza" },
  { v: "settimanaMese",      label: "Settimana del mese" },
  { v: "cap",                label: "CAP" },
  { v: "dataScadenza",       label: "Scadenza contratto" },
];

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function StatCard({ label, value, testid, accent }) {
  return (
    <Card className="p-3 bg-white border border-slate-200" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: accent || "#1B5E20", fontFamily: "Outfit, sans-serif" }}>
        {value}
      </div>
    </Card>
  );
}

export default function ProduttoriDaRivedere() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { zone } = useZoneAttive();

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingSum, setLoadingSum] = useState(true);
  const [loadingList, setLoadingList] = useState(true);

  // Filters from URL
  const q = searchParams.get("q") || "";
  const zona = searchParams.get("zona") || "";
  const area = searchParams.get("area") || "";
  const freq = searchParams.get("freq") || "";
  const settimana = searchParams.get("sett") || "";
  const senzaTel = searchParams.get("nophone") === "1";
  const geoMan = searchParams.get("nogeo") === "1";
  const batch = searchParams.get("batch") || "";

  const sortBy = searchParams.get("sort") || "ragioneSociale";
  const sortDir = searchParams.get("dir") || "asc";

  const page = parseInt(searchParams.get("p") || "1", 10);
  const pageSize = parseInt(searchParams.get("ps") || "50", 10);

  const [qInput, setQInput] = useState(q);
  const qDeb = useDebounced(qInput, 350);

  useEffect(() => {
    setLoadingSum(true);
    getProduttoriDaRivedereSummary(batch || undefined)
      .then(setSummary)
      .finally(() => setLoadingSum(false));
  }, [batch]);

  const reload = () => {
    setLoadingList(true);
    const params = {
      frequenzaDaVerificare: true,
      paginated: true,
      limit: pageSize,
      skip: (page - 1) * pageSize,
      sort_by: sortBy,
      sort_dir: sortDir,
    };
    if (qDeb) params.q = qDeb;
    if (zona) params.zona = zona;
    if (area) params.areaClassificazione = area;
    if (freq) params.frequenza = freq;
    if (settimana) params.settimanaMese = settimana;
    if (senzaTel) params.senza_telefono = true;
    if (geoMan) params.geocoding_mancante = true;
    if (batch) params.importBatchId = batch;

    getProduttori(params)
      .then((r) => {
        if (r && typeof r === "object" && Array.isArray(r.items)) {
          setItems(r.items);
          setTotal(r.total || 0);
        } else {
          setItems([]);
          setTotal(0);
        }
      })
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDeb, zona, area, freq, settimana, senzaTel, geoMan, batch, sortBy, sortDir, page, pageSize]);

  // Sync q to URL with debounce
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (qDeb) next.set("q", qDeb); else next.delete("q");
    next.delete("p");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDeb]);

  const setF = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "" || value === false) next.delete(key);
    else next.set(key, value === true ? "1" : String(value));
    next.delete("p");
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    setQInput("");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const sortedZoneNames = useMemo(() => {
    if (!summary?.per_zona) return [];
    return Object.entries(summary.per_zona)
      .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]));
  }, [summary]);

  return (
    <div className="space-y-5" data-testid="produttori-da-rivedere-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Produttori da rivedere
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Elenco dei produttori importati che richiedono controllo della frequenza di ritiro
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { reload(); setLoadingSum(true); getProduttoriDaRivedereSummary(batch || undefined).then(setSummary).finally(() => setLoadingSum(false)); }}
            data-testid="btn-refresh"
          >
            <RotateCw size={14} className="mr-1" /> Aggiorna
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {loadingSum || !summary ? (
        <Card className="p-6 text-slate-500" data-testid="summary-loading">Caricamento riepilogo…</Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="summary-grid">
            <StatCard label="Totale" value={summary.total} testid="sum-total" />
            <StatCard label="A — Normale" value={summary.per_area?.A_NORMALE || 0} accent="#1B5E20" testid="sum-area-a" />
            <StatCard label="B — Estesa" value={summary.per_area?.B_ESTESA || 0} accent="#F57C00" testid="sum-area-b" />
            <StatCard label="C — Fuori area" value={summary.per_area?.C_FUORI_AREA || 0} accent="#C62828" testid="sum-area-c" />
            <StatCard label="Senza telefono" value={summary.senza_telefono} accent="#7B1FA2" testid="sum-no-phone" />
            <StatCard label="Geocoding mancante" value={summary.geocoding_mancante} accent="#1565C0" testid="sum-no-geo" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="summary-detail-grid">
            <Card className="p-4 bg-white border border-slate-200" data-testid="sum-frequenza-card">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Per frequenza</div>
              <div className="flex flex-wrap gap-2">
                {FREQUENZE.map((f) => (
                  <Badge
                    key={f}
                    data-testid={`sum-freq-${f}`}
                    className="bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs"
                  >
                    {FREQUENZE_LABEL[f]}: <b className="ml-1">{summary.per_frequenza?.[f] || 0}</b>
                  </Badge>
                ))}
              </div>
            </Card>

            <Card className="p-4 bg-white border border-slate-200" data-testid="sum-settimana-card">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Per settimana del mese</div>
              <div className="flex flex-wrap gap-2">
                {["W1", "W2", "W3", "W4"].map((w) => (
                  <Badge key={w} data-testid={`sum-sett-${w}`} className="bg-slate-100 text-slate-700 text-xs">
                    {w}: <b className="ml-1">{summary.per_settimana_mese?.[w] || 0}</b>
                  </Badge>
                ))}
                {summary.settimana_mese_non_applicabile > 0 && (
                  <Badge
                    data-testid="sum-sett-non-applicabile"
                    className="bg-slate-100 text-slate-500 text-xs cursor-pointer"
                    title="Frequenza per cui settimanaMese non si applica (SETTIMANALE/BISETTIMANALE/SU_APPUNTAMENTO). Clic: filtra BISETTIMANALE"
                    onClick={() => setF("freq", "BISETTIMANALE")}
                  >
                    Non applicabile: <b className="ml-1">{summary.settimana_mese_non_applicabile}</b>
                  </Badge>
                )}
                {summary.settimana_mese_non_impostata > 0 && (
                  <Badge
                    data-testid="sum-sett-non-impostata"
                    className="bg-amber-100 text-amber-800 text-xs cursor-pointer"
                    title="MENSILE o BIMESTRALE senza settimanaMese — record da fixare. Clic: filtra"
                    onClick={() => setF("sett", "__NULL__")}
                  >
                    Non impostata: <b className="ml-1">{summary.settimana_mese_non_impostata}</b>
                  </Badge>
                )}
              </div>
            </Card>
          </div>

          <Card className="p-4 bg-white border border-slate-200" data-testid="sum-zone-card">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Per zona ({sortedZoneNames.length})
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {sortedZoneNames.map(([name, n]) => (
                <Badge
                  key={name}
                  data-testid={`sum-zona-${name}`}
                  className="bg-slate-100 text-slate-700 hover:bg-slate-200 text-[11px] cursor-pointer"
                  onClick={() => setF("zona", name)}
                >
                  {name}: <b className="ml-1">{n}</b>
                </Badge>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Filters */}
      <Card className="p-4 bg-white border border-slate-200" data-testid="filters-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-xs">Cerca per ragione sociale, indirizzo, unità produttiva o CAP</Label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="filter-q"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Es. Ristorante, Via Roma, 50122…"
                className="pl-9 h-10"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Zona</Label>
            <Select value={zona || "ALL"} onValueChange={(v) => setF("zona", v === "ALL" ? "" : v)}>
              <SelectTrigger data-testid="filter-zona"><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutte le zone</SelectItem>
                {zone.map((z) => <SelectItem key={z.id} value={z.nome}>{z.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Area</Label>
            <Select value={area || "ALL"} onValueChange={(v) => setF("area", v === "ALL" ? "" : v)}>
              <SelectTrigger data-testid="filter-area"><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutte</SelectItem>
                {AREE.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Frequenza</Label>
            <Select value={freq || "ALL"} onValueChange={(v) => setF("freq", v === "ALL" ? "" : v)}>
              <SelectTrigger data-testid="filter-freq"><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutte</SelectItem>
                {FREQUENZE.map((f) => <SelectItem key={f} value={f}>{FREQUENZE_LABEL[f]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Settimana del mese</Label>
            <Select value={settimana || "ALL"} onValueChange={(v) => setF("sett", v === "ALL" ? "" : v)}>
              <SelectTrigger data-testid="filter-sett"><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutte</SelectItem>
                {SETTIMANE_MESE.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Batch import</Label>
            <Select value={batch || "ALL"} onValueChange={(v) => setF("batch", v === "ALL" ? "" : v)}>
              <SelectTrigger data-testid="filter-batch"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutti</SelectItem>
                {(summary?.import_batches || []).map((b) => (
                  <SelectItem key={b} value={b}>{b.substring(0, 8)}…</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end gap-3 lg:col-span-2">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer h-10">
              <input
                type="checkbox"
                data-testid="filter-no-phone"
                checked={senzaTel}
                onChange={(e) => setF("nophone", e.target.checked)}
                className="h-4 w-4"
              />
              <PhoneOff size={14} className="text-purple-700" />
              Solo senza telefono
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer h-10">
              <input
                type="checkbox"
                data-testid="filter-no-geo"
                checked={geoMan}
                onChange={(e) => setF("nogeo", e.target.checked)}
                className="h-4 w-4"
              />
              <MapPinOff size={14} className="text-blue-700" />
              Solo geocoding mancante
            </label>
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="btn-clear-filters">
              Pulisci filtri
            </Button>
          </div>
        </div>
      </Card>

      {/* Counter + sort */}
      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="result-bar">
        <div className="text-sm text-slate-600 font-semibold" data-testid="result-counter">
          <b>{total}</b> produttori da verificare
          {total !== summary?.total && summary?.total ? (
            <span className="text-slate-400 font-normal"> (di {summary.total})</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Ordina per</Label>
          <Select value={sortBy} onValueChange={(v) => setF("sort", v === "ragioneSociale" ? "" : v)}>
            <SelectTrigger className="h-9 w-44" data-testid="sort-by"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            data-testid="sort-dir"
            onClick={() => setF("dir", sortDir === "asc" ? "desc" : "")}
            title={sortDir === "asc" ? "Crescente" : "Decrescente"}
          >
            {sortDir === "asc" ? <ArrowDown01 size={14} /> : <ArrowUp10 size={14} />}
          </Button>
        </div>
      </div>

      {/* List */}
      {loadingList ? (
        <Card className="p-6 text-slate-500" data-testid="list-loading">Caricamento…</Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="list-empty">
          Nessun produttore da verificare con i filtri attuali.
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="produttori-list">
          {items.map((p) => {
            const c = p.contratto || {};
            const noTel = !p.telefonoFisso && !p.telefonoMobile;
            const noGeo = p.latitudine == null || p.longitudine == null;
            const areaCfg = AREE.find((a) => a.v === p.areaClassificazione);
            return (
              <li key={p.id}>
                <Link to={`/produttori/${p.id}`} data-testid={`row-${p.id}`} className="block">
                  <Card className="p-3 bg-white border border-slate-200 card-hover">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-slate-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {p.ragioneSociale}
                          </div>
                          <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-200 text-[10px]" data-testid={`badge-da-verificare-${p.id}`}>
                            <AlertTriangle size={10} className="mr-1" /> DA VERIFICARE
                          </Badge>
                          {areaCfg && (
                            <Badge
                              data-testid={`badge-area-${p.id}`}
                              className="text-[10px] text-white"
                              style={{ backgroundColor: areaCfg.color }}
                            >
                              {areaCfg.label}
                            </Badge>
                          )}
                          {c.frequenza === "BIMESTRALE" && (
                            <Badge className="bg-indigo-100 text-indigo-900 text-[10px]" data-testid={`badge-bimestrale-${p.id}`}>
                              BIMESTRALE
                            </Badge>
                          )}
                          {c.frequenza === "MENSILE" && (
                            <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-testid={`badge-mensile-prov-${p.id}`}>
                              MENSILE provvisorio
                            </Badge>
                          )}
                          {noTel && (
                            <Badge className="bg-purple-100 text-purple-900 text-[10px]" data-testid={`badge-no-phone-${p.id}`}>
                              <PhoneOff size={10} className="mr-1" /> tel mancante
                            </Badge>
                          )}
                          {noGeo && (
                            <Badge className="bg-blue-100 text-blue-900 text-[10px]" data-testid={`badge-no-geo-${p.id}`}>
                              <MapPinOff size={10} className="mr-1" /> geo mancante
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span><b>Zona:</b> {p.zona || "—"}</span>
                          <span><b>CAP:</b> {p.cap || "—"}</span>
                          <span><b>Freq:</b> {FREQUENZE_LABEL[c.frequenza] || c.frequenza || "—"}</span>
                          <span><b>Sett:</b> {c.settimanaMese || "—"}</span>
                          {c.frequenza === "BIMESTRALE" && (
                            <span><b>Mesi:</b> {c.meseAlternanza || "—"}</span>
                          )}
                          <span><b>Tel:</b> {p.telefonoMobile || p.telefonoFisso || "—"}</span>
                          <span><b>Scadenza:</b> {formatDateIt(c.dataScadenza)}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                          {p.indirizzo || "—"}{p.citta ? ` · ${p.citta}` : ""}
                        </div>
                      </div>
                      <ChevronRight size={20} className="text-slate-400 mt-1 shrink-0" />
                    </div>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {/* Pagination */}
      {!loadingList && total > 0 && (
        <div className="flex items-center justify-between gap-3 py-3 border-t border-slate-200" data-testid="pagination-bar">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span>Per pagina:</span>
            {[50, 100, 200].map((n) => (
              <button
                key={n}
                onClick={() => setF("ps", n === 50 ? "" : String(n))}
                data-testid={`page-size-${n}`}
                className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                  pageSize === n ? "bg-[#1B5E20] text-white" : "bg-white border border-slate-200 text-slate-700"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setF("p", String(Math.max(1, page - 1)))}
              disabled={page <= 1}
              data-testid="page-prev"
              className="h-9 px-3 rounded-md text-sm bg-white border border-slate-200 text-slate-700 disabled:opacity-40 min-h-[36px]"
            >
              ← Prec
            </button>
            <span className="text-xs text-slate-600" data-testid="page-indicator">
              Pagina <b>{page}</b> di <b>{totalPages}</b>
            </span>
            <button
              onClick={() => setF("p", String(Math.min(totalPages, page + 1)))}
              disabled={page >= totalPages}
              data-testid="page-next"
              className="h-9 px-3 rounded-md text-sm bg-white border border-slate-200 text-slate-700 disabled:opacity-40 min-h-[36px]"
            >
              Succ →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
